/**
 * CollapsibleFilterSection - Expandable section wrapper for filter groups
 *
 * Features:
 * - Click to expand/collapse content
 * - Shows active filter count badge when collapsed
 * - Smooth animation for expand/collapse
 * - Consistent styling across all filter sections
 */

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { type ReactNode, useState } from 'react';

import { cn } from '@/lib/utils';

interface CollapsibleFilterSectionProps {
  title: string;
  activeCount?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}

export const CollapsibleFilterSection = ({
  title,
  activeCount = 0,
  defaultOpen = false,
  children,
}: CollapsibleFilterSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="px-6 py-3">
      {/* Section Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-zinc-950 dark:text-white">
            {title}
          </span>
          {activeCount > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-zinc-700 dark:bg-zinc-500 px-1 text-[10px] font-medium text-white">
              {activeCount}
            </span>
          )}
        </div>
        <span className="text-zinc-400 dark:text-zinc-500">
          {isOpen ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </span>
      </button>

      {/* Collapsible Content */}
      {/* Note: overflow-visible when expanded allows dropdowns/calendars to escape the container */}
      <div
        className={cn(
          'transition-all duration-200 ease-in-out',
          isOpen
            ? 'mt-4 max-h-96 opacity-100 overflow-visible'
            : 'max-h-0 opacity-0 overflow-hidden',
        )}
      >
        {children}
      </div>
    </div>
  );
};
